import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import ArrowDiagonalIcon from '../../../icons/react/icon-arrow-diag';
import { HorizontalStack, type HorizontalStackProps } from '../../alignment/horizontal-stack';
import { CopyButton } from '../../copy-button/copy-button';
import type { LinkElementType } from '../../link/base-link';
import { TextLink } from '../../link/text-link/text-link';
import { Tooltip } from '../../tooltip/tooltip';
import { Typography, type TypographyProps } from '../../typography';
import { getShortenedId } from './shared';
import styles from './id.module.scss';

export interface IDProps extends HorizontalStackProps {
  canCopy?: boolean;
  external?: boolean;
  value: string | undefined;
  inline?: boolean;
  LinkElement?: LinkElementType;
  preload?: boolean;
  renderWhenEmpty?: ReactNode;
  short?: boolean;
  to?: string;
  state?: any;
  typographyProps?: TypographyProps;
}

export function ID({
  canCopy = true,
  external,
  value,
  inline,
  LinkElement = 'a',
  renderWhenEmpty = null,
  preload,
  short,
  state,
  to,
  typographyProps,
  ...rest
}: IDProps) {
  const idToRender = short ? getShortenedId(value) : value;

  if (!idToRender) {
    return renderWhenEmpty;
  }

  const renderedElement =
    value && to ? (
      <TextLink LinkElement={LinkElement} preload={preload} state={state} to={to} typography={typographyProps}>
        <HorizontalStack blockAlign="center" gap={1} wrap="nowrap">
          {idToRender}
          {external ? <ArrowDiagonalIcon aria-hidden height={16} /> : null}
        </HorizontalStack>
      </TextLink>
    ) : (
      <Typography {...typographyProps}>{idToRender}</Typography>
    );

  return (
    <HorizontalStack blockAlign="center" className={clsx(styles.id, inline && styles.inline)} gap={4} wrap="nowrap" {...rest}>
      {short ? <Tooltip activator={to ? renderedElement : <button type="button">{renderedElement}</button>}>{value}</Tooltip> : renderedElement}
      {value && canCopy ? <CopyButton content={value} label="Copy ID" tooltip /> : null}
    </HorizontalStack>
  );
}
